import {
  DEFAULT_PAGE_SIZE,
  EventListQuerySchema,
  type EventListQuery,
} from "@cdt/shared";

const DEFAULT_QUERY: EventListQuery = EventListQuerySchema.parse({});

function paramOrUndefined(
  sp: URLSearchParams,
  key: string,
): string | undefined {
  const value = sp.get(key);
  return value === null ? undefined : value;
}

/**
 * Parses URLSearchParams into a validated EventListQuery. Falls back to
 * defaults whenever the URL contains invalid values, so the UI never crashes
 * on hand-edited or stale URLs.
 */
export function parseSearchParams(sp: URLSearchParams): EventListQuery {
  const raw = {
    status: paramOrUndefined(sp, "status"),
    dateFrom: paramOrUndefined(sp, "dateFrom"),
    dateTo: paramOrUndefined(sp, "dateTo"),
    startDateFrom: paramOrUndefined(sp, "startDateFrom"),
    startDateTo: paramOrUndefined(sp, "startDateTo"),
    search: paramOrUndefined(sp, "search"),
    page: paramOrUndefined(sp, "page"),
    pageSize: paramOrUndefined(sp, "pageSize"),
    sortBy: paramOrUndefined(sp, "sortBy"),
    sortOrder: paramOrUndefined(sp, "sortOrder"),
    priority: paramOrUndefined(sp, "priority"),
    priorityMin: paramOrUndefined(sp, "priorityMin"),
    categoryId: paramOrUndefined(sp, "categoryId"),
    unseen: paramOrUndefined(sp, "unseen"),
    isImportant: paramOrUndefined(sp, "isImportant"),
    hideMultiDay: paramOrUndefined(sp, "hideMultiDay"),
    uncategorized: paramOrUndefined(sp, "uncategorized"),
  };
  const result = EventListQuerySchema.safeParse(raw);
  return result.success ? result.data : DEFAULT_QUERY;
}

/**
 * Serializes a query back into URLSearchParams, omitting values equal to the
 * default so that the URL stays clean (e.g. no ?sortBy=emailReceivedAt).
 */
export function queryToSearchParams(query: EventListQuery): URLSearchParams {
  const sp = new URLSearchParams();
  if (query.status && query.status.length > 0) {
    sp.set("status", query.status.join(","));
  }
  if (query.dateFrom) sp.set("dateFrom", query.dateFrom);
  if (query.dateTo) sp.set("dateTo", query.dateTo);
  if (query.startDateFrom) sp.set("startDateFrom", query.startDateFrom);
  if (query.startDateTo) sp.set("startDateTo", query.startDateTo);
  if (query.search) sp.set("search", query.search);
  if (query.page !== DEFAULT_QUERY.page) sp.set("page", String(query.page));
  if (query.pageSize !== DEFAULT_PAGE_SIZE) {
    sp.set("pageSize", String(query.pageSize));
  }
  if (query.sortBy !== DEFAULT_QUERY.sortBy) sp.set("sortBy", query.sortBy);
  if (query.sortOrder !== DEFAULT_QUERY.sortOrder) {
    sp.set("sortOrder", query.sortOrder);
  }
  if (query.priority && query.priority.length > 0) {
    sp.set("priority", query.priority.join(","));
  }
  if (query.priorityMin !== undefined) {
    sp.set("priorityMin", String(query.priorityMin));
  }
  if (query.categoryId && query.categoryId.length > 0) {
    sp.set("categoryId", query.categoryId.join(","));
  }
  if (query.unseen === true) {
    sp.set("unseen", "true");
  }
  if (query.isImportant === true) {
    sp.set("isImportant", "true");
  }
  if (query.hideMultiDay === true) {
    sp.set("hideMultiDay", "true");
  }
  if (query.uncategorized === true) {
    sp.set("uncategorized", "true");
  }
  return sp;
}

export function isDefaultQuery(query: EventListQuery): boolean {
  return (
    (!query.status || query.status.length === 0) &&
    !query.dateFrom &&
    !query.dateTo &&
    !query.search &&
    (!query.priority || query.priority.length === 0) &&
    query.priorityMin === undefined &&
    query.unseen !== true &&
    query.isImportant !== true &&
    query.hideMultiDay !== true &&
    query.uncategorized !== true
  );
}

export { DEFAULT_QUERY };
